import type {
  WorkbookDiagnostic,
  WorkbookDiagnosticCode,
  WorkbookSheet,
  WorkbookValidation,
  WorkbookValidationResult,
} from "@erp/shared-types";
import { sheetKind } from "./sheetKinds";
import {
  parseFormula,
  typeFormula,
  type ColumnType,
  type TypedFormula,
} from "./formula";

/** A Validation ready to check: the Declared type of its column, and a Formula's typed form. */
export interface PlannedValidation {
  index: number;
  validation: WorkbookValidation;
  type: ColumnType;
  /** A Formula Validation's Formula, typed against the Worksheet's columns. */
  formula?: TypedFormula;
}

/** RE2 (the engine's regular expressions) has no lookaround and no backreferences. */
const UNSUPPORTED_PATTERN = /\(\?<?[=!]|\\[1-9]|\\k</;

function patternProblem(pattern: string): string | null {
  try {
    new RegExp(pattern, "u");
  } catch {
    return "This pattern isn't a valid regular expression.";
  }
  return UNSUPPORTED_PATTERN.test(pattern)
    ? "Patterns can't look ahead, look behind or refer back to a group."
    : null;
}

/** A Validation's problem, located within it by the planner. */
interface RuleProblem {
  code: WorkbookDiagnosticCode;
  message: string;
  span?: { start: number; end: number };
}
/** A sound Validation: a Formula one carries its typed Formula. */
type RuleChecked = { formula?: TypedFormula };

function problem(
  code: WorkbookDiagnosticCode,
  message: string,
  span?: { start: number; end: number },
): RuleProblem {
  return span ? { code, message, span } : { code, message };
}

/** A Date or a Date and time: a type whose values order in time. */
export function isDateColumnType(type: ColumnType): boolean {
  return type === "date" || type === "datetime";
}

function checkRange(
  validation: Extract<WorkbookValidation, { kind: "range" }>,
  type: ColumnType,
): RuleProblem | RuleChecked {
  if (!validation.min && !validation.max)
    return problem(
      "validation-range",
      "A range needs a smallest value, a largest value, or both.",
    );
  return type === "decimal" || isDateColumnType(type)
    ? {}
    : problem(
        "validation-range",
        "A range checks a Number, Date or Date and time column.",
      );
}

function checkFormula(
  expression: string,
  fields: ReadonlyMap<string, ColumnType>,
): RuleProblem | RuleChecked {
  const parsed = parseFormula(expression);
  if (!parsed.ok)
    return problem(
      parsed.problem.code,
      parsed.problem.message,
      parsed.problem.span,
    );
  const typed = typeFormula(parsed.formula, fields);
  if (!typed.ok)
    return problem(
      typed.problem.code,
      typed.problem.message,
      typed.problem.span,
    );
  return typed.formula.type === "boolean"
    ? { formula: typed.formula }
    : problem(
        "validation-not-boolean",
        "A validation formula must give TRUE or FALSE, such as [@Qty] > 0.",
      );
}

/** What each kind needs beyond a known column. */
function checkRule(
  validation: WorkbookValidation,
  type: ColumnType,
  fields: ReadonlyMap<string, ColumnType>,
): RuleProblem | RuleChecked {
  switch (validation.kind) {
    case "accepted-values":
      return validation.values.length
        ? {}
        : problem(
            "empty-value-list",
            `The validation on '${validation.column}' lists no values.`,
          );
    case "range":
      return checkRange(validation, type);
    case "pattern": {
      const message = patternProblem(validation.pattern);
      return message ? problem("validation-pattern", message) : {};
    }
    case "formula":
      return checkFormula(validation.expression, fields);
    default:
      return {};
  }
}

/**
 * Plans a Worksheet's Validations against its Declared output columns, the
 * columns of its Result. Each problem is a Diagnostic located on the
 * Validation, and blocks a Run like any other; only sound Validations are
 * planned.
 */
export function planValidations(sheet: WorkbookSheet): {
  validations: PlannedValidation[];
  diagnostics: WorkbookDiagnostic[];
} {
  const kind = sheetKind(sheet);
  const fields = new Map(
    kind.resultColumns().map((column) => [column.name, column.type]),
  );
  // A rule on a column only its data names (a pivot's unpinned Column keys)
  // is checked when a Run has it.
  const dataNamed = kind.dataNamedColumnType();
  if (dataNamed)
    for (const rule of sheet.validations ?? [])
      if (!fields.has(rule.column)) fields.set(rule.column, dataNamed);
  return planResultValidations(sheet.id, sheet.validations ?? [], fields);
}

/**
 * {@link planValidations} for a Result of any sheet kind: its Validations
 * against the Result's columns and their types.
 */
export function planResultValidations(
  itemId: string,
  rules: readonly WorkbookValidation[],
  fields: ReadonlyMap<string, ColumnType>,
): {
  validations: PlannedValidation[];
  diagnostics: WorkbookDiagnostic[];
} {
  const validations: PlannedValidation[] = [];
  const diagnostics: WorkbookDiagnostic[] = [];
  rules.forEach((validation, index) => {
    const type = fields.get(validation.column);
    const checked =
      type === undefined
        ? problem("unknown-field", `Unknown column '${validation.column}'.`)
        : checkRule(validation, type, fields);
    if ("code" in checked) {
      const { code, message, span } = checked;
      diagnostics.push({
        code,
        message,
        location: {
          part: "validation",
          itemId,
          index,
          ...(span ? { span } : {}),
        },
      });
    } else if (type !== undefined && !isRelationalValidation(validation))
      // A relational rule reads another Result: checked after the transform.
      validations.push({ index, validation, type, ...checked });
  });
  return { validations, diagnostics };
}

/** A Validation relating a Worksheet's Result to another's (`in-sheet`). */
export type RelationalValidation = Extract<
  WorkbookValidation,
  { kind: "in-sheet" }
>;

/** Whether a Validation relates its Result to another Worksheet's, so the transform doesn't check it. */
export function isRelationalValidation(
  validation: WorkbookValidation,
): validation is RelationalValidation {
  return validation.kind === "in-sheet";
}

/**
 * Diagnostics of relational Validations: a rule naming a Worksheet that
 * doesn't exist (or its own), or a column that Worksheet's Result doesn't
 * have (unless its data names columns, as a pivot's may).
 */
export function relationalValidationDiagnostics(
  sheets: readonly WorkbookSheet[],
): WorkbookDiagnostic[] {
  return sheets.flatMap((sheet) =>
    (sheet.validations ?? []).flatMap(
      (validation, index): WorkbookDiagnostic[] => {
        if (!isRelationalValidation(validation)) return [];
        const other = sheets.find((item) => item.id === validation.sheetId);
        const at = (message: string): WorkbookDiagnostic[] => [
          {
            code: "validation-sheet",
            message,
            location: { part: "validation", itemId: sheet.id, index },
          },
        ];
        if (!other || other.id === sheet.id)
          return at(
            `The validation on '${validation.column}' compares with a worksheet that doesn't exist.`,
          );
        const kind = sheetKind(other);
        const lower = validation.matchColumn.toLowerCase();
        const known = kind
          .resultColumns()
          .some((column) => column.name.toLowerCase() === lower);
        return known || kind.dataNamedColumnType()
          ? []
          : at(`'${other.name}' has no column '${validation.matchColumn}'.`);
      },
    ),
  );
}

/** A Validation in words, for messages and the agent: "Code is unique". */
export function describeValidation(validation: WorkbookValidation): string {
  const column = `'${validation.column}'`;
  switch (validation.kind) {
    case "not-empty":
      return `${column} is not empty`;
    case "unique":
      return `${column} is unique`;
    case "accepted-values":
      return `${column} is one of ${validation.values.map((value) => `'${value}'`).join(", ")}`;
    case "range":
      if (validation.min && validation.max)
        return `${column} is between ${validation.min} and ${validation.max}`;
      return validation.min
        ? `${column} is at least ${validation.min}`
        : `${column} is at most ${validation.max ?? ""}`;
    case "pattern":
      return `${column} matches /${validation.pattern}/`;
    case "no-errors":
      return `${column} has no errors`;
    case "formula":
      return `=${validation.expression} is TRUE`;
    case "in-sheet":
      return `${column} appears ${validation.exactlyOnce ? "exactly once " : ""}in '${validation.matchColumn}' of another worksheet`;
  }
}

/**
 * How a Run's Validations fared across the Results it holds: `failed` when an
 * error-severity Validation failed, `warned` when only warnings did,
 * `passed` when none did, and null when it checked none.
 */
export function validationOutcome(
  results: readonly { validations?: readonly WorkbookValidationResult[] }[],
): "passed" | "warned" | "failed" | null {
  const checked = results.flatMap((result) => result.validations ?? []);
  if (!checked.length) return null;
  const failed = checked.filter((result) => result.failedRowCount > 0);
  if (failed.some((result) => result.validation.severity === "error"))
    return "failed";
  return failed.length ? "warned" : "passed";
}
